import { computed, reactive, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useDirtyState as useDirtyStateCore } from "@eudonet/opal";
import { logger } from "@/infrastructure/services/LoggerService";
import { createContactIdentifier, deleteContactIdentifier, updateContactIdentifier, } from "../infrastructure/api/contactIdentifierApi";
/** Maps each stable error code to its i18n message key. */
const ERROR_MESSAGE_KEY = {
    "Contact.Identifier.Value.TooLong": "contact.detail.identifiers.errors.valueTooLong",
};
/**
 * Maps each editable field to the OPHUB `ContactIdentifierType` enum member name
 * sent on the wire.
 */
const FIELD_TYPE = {
    eudonetId: "EudonetId",
    ineNumber: "INE",
    lmsId: "LMS",
    parcoursupId: "Parcoursup",
    campusFranceNumber: "CampusFrance",
};
const FIELDS = Object.keys(FIELD_TYPE);
// OPHUB caps the identifier value at 255 chars — mirror it FE-side as a guard.
const MAX_VALUE_LENGTH = 255;
/**
 * Identifiants bloc form logic (OP-5564): local state for the five fixed
 * identifier types, per-type validation with stable error codes, and a per-bloc
 * `save()` that fans out create / update / delete to the BFF identifier
 * endpoints. Plugged into the page save orchestrator (OP-5555). Kept
 * framework-light (no DOM) so it is unit-testable in isolation.
 *
 * @param contact The loaded contact aggregate. The form re-populates and
 *   re-baselines whenever it resolves so freshly fetched data is not dirty.
 * @param contactId The contact id used for the write calls.
 */
export function useContactIdentifiersForm(contact, contactId) {
    const { t } = useI18n();
    const form = reactive({
        eudonetId: "",
        ineNumber: "",
        lmsId: "",
        parcoursupId: "",
        campusFranceNumber: "",
    });
    // Server-truth snapshot per field (row id + value), used to decide
    // create/update/delete on save and to skip unchanged fields. Distinct from
    // the dirty tracker's baseline, which snapshots `form` for the dirty flag.
    const baseline = reactive({
        eudonetId: { id: null, value: "" },
        ineNumber: { id: null, value: "" },
        lmsId: { id: null, value: "" },
        parcoursupId: { id: null, value: "" },
        campusFranceNumber: { id: null, value: "" },
    });
    // `enableBeforeUnload: false` — the page-level orchestrator owns the single
    // `beforeunload` listener covering every registered bloc.
    const { isDirty, markAsSaved, resetToOriginal } = useDirtyStateCore({
        data: form,
        enableBeforeUnload: false,
    });
    const errorCodes = ref({});
    const errors = computed(() => {
        const out = {};
        for (const [field, code] of Object.entries(errorCodes.value)) {
            out[field] = t(ERROR_MESSAGE_KEY[code]);
        }
        return out;
    });
    /** Returns the error code for a single field's value, or undefined when valid. */
    function validateField(value) {
        // Empty is always valid — it means "delete / no identifier of this type".
        if (!value)
            return undefined;
        if (value.length > MAX_VALUE_LENGTH)
            return "Contact.Identifier.Value.TooLong";
        return undefined;
    }
    // Side-effect-free validity of the current values — gates the page Save button
    // without mutating the surfaced `errorCodes` (which still only fill on save).
    const isValid = computed(() => FIELDS.every((f) => !validateField(form[f].trim())));
    function validate() {
        const next = {};
        for (const field of FIELDS) {
            const code = validateField(form[field].trim());
            if (code)
                next[field] = code;
        }
        errorCodes.value = next;
        return Object.keys(next).length === 0;
    }
    /** Persists a single changed field and returns its new baseline state. */
    async function persistField(field) {
        const type = FIELD_TYPE[field];
        const newValue = form[field].trim();
        const base = baseline[field];
        if (base.id && !newValue) {
            await deleteContactIdentifier(contactId.value, base.id);
            return { id: null, value: "" };
        }
        if (base.id) {
            await updateContactIdentifier(contactId.value, base.id, { type, value: newValue });
            return { id: base.id, value: newValue };
        }
        // base.id === null and newValue is non-empty (the changed filter guarantees
        // empty→empty never reaches here) — create.
        const id = await createContactIdentifier(contactId.value, { type, value: newValue });
        return { id, value: newValue };
    }
    async function save() {
        if (!contactId.value)
            throw new Error("Cannot save identifiers before the contact has loaded");
        const changed = FIELDS.filter((f) => form[f].trim() !== baseline[f].value);
        if (changed.length === 0) {
            markAsSaved();
            return;
        }
        // Per-field validation: collect codes for the changed fields. Valid fields
        // still proceed; invalid ones keep their inline error and are skipped.
        const next = {};
        for (const field of changed) {
            const code = validateField(form[field].trim());
            if (code)
                next[field] = code;
        }
        errorCodes.value = next;
        const toPersist = changed.filter((f) => !next[f]);
        const results = await Promise.allSettled(toPersist.map((f) => persistField(f)));
        let anyFailed = false;
        results.forEach((result, index) => {
            const field = toPersist[index];
            if (result.status === "fulfilled") {
                // Advance the server-truth baseline so a retry never re-creates a row
                // that already persisted (which would 409) and a succeeded field is not
                // resubmitted.
                baseline[field] = result.value;
                form[field] = result.value.value;
            }
            else {
                anyFailed = true;
                logger.error(`[useContactIdentifiersForm] persist failed for "${field}"`, result.reason);
            }
        });
        // Clean only when nothing was invalid and nothing failed; otherwise stay
        // dirty + throw so the orchestrator emits the per-bloc error toast.
        if (Object.keys(next).length === 0 && !anyFailed) {
            markAsSaved();
            return;
        }
        throw new Error("Contact identifiers save partially failed");
    }
    // Populate + re-baseline when the contact resolves or we switch to a different
    // contact. A *same-contact* re-fetch (e.g. a sibling bloc's save() triggers a
    // page-level reload of the shared aggregate) MUST NOT clobber this bloc's
    // in-progress edits or wipe its inline validation errors — otherwise an
    // over-long value typed here vanishes the instant the Identité bloc saves and
    // the user never sees the error. Re-initialise only on a contact change, or when
    // this bloc is clean (nothing unsaved to preserve).
    let lastContactId = null;
    watch(contact, (loaded) => {
        if (!loaded)
            return;
        const sameContact = loaded.id === lastContactId;
        if (sameContact && (isDirty.value || Object.keys(errorCodes.value).length > 0)) {
            return;
        }
        lastContactId = loaded.id;
        for (const field of FIELDS) {
            const found = loaded.identifiers.find((i) => i.type === FIELD_TYPE[field]);
            const value = found?.value ?? "";
            form[field] = value;
            baseline[field] = { id: found?.id ?? null, value };
        }
        errorCodes.value = {};
        markAsSaved();
    }, { immediate: true });
    return { form, isDirty, isValid, errorCodes, errors, validate, save, markAsSaved, resetToOriginal };
}
